import type { ScenarioSpec } from "./types";

/**
 * Walkable office — a REAL purchased glTF room (genuine multi-surface mesh with
 * real parallax everywhere), WASD first-person, AABB collision.
 *
 * Model: a furnished modern-office VR asset whose ALBEDO textures already have
 * soft window light + cast shadows painted in (baked lighting) and real normal
 * maps, so it reads as photoreal in real-time WebGL with no offline renderer.
 *
 * Native model coordinate space (NOT re-centred): floor at y=0, walls to y~3.5.
 * Interior floor union spans X ~ [-12.8, 8.3], Z ~ [-10.0, 6.4]. A big Pano360
 * sphere (±50) and an X01_Lighting helper are ignored for collision.
 */

// Outer containment rectangle (trimmed slightly inside the modelled walls).
const ROOM_MIN_X = -12.4;
const ROOM_MAX_X = 7.9;
const ROOM_MIN_Z = -9.7;
const ROOM_MAX_Z = 6.2;
const WALL_H = 3.4;
const PAD = 0.6;

export const officeWalkableScenario: ScenarioSpec = {
  version: 1,
  id: "office-walkable",
  title: "Walkable Office",
  topic: "WASD walk inside a real purchased 3D office room with true collision",
  language: "en",
  ambientUrl: "",
  ambientVolume: 0.06,
  music: "office",
  walkable: true,
  riskMeter: false,
  passThreshold: 0,

  scene: {
    environment: {
      gltfUrl: "/cdn/sim/office-vr/office.glb",
      gltfScale: 1,
      gltfPosition: [0, 0, 0],
      gltfRotationDeg: [0, 0, 0],
      gltfColliders: [
        // West wall
        { min: [ROOM_MIN_X - PAD, 0, ROOM_MIN_Z - PAD], max: [ROOM_MIN_X, WALL_H, ROOM_MAX_Z + PAD] },
        // East wall
        { min: [ROOM_MAX_X, 0, ROOM_MIN_Z - PAD], max: [ROOM_MAX_X + PAD, WALL_H, ROOM_MAX_Z + PAD] },
        // South wall (more-negative Z)
        { min: [ROOM_MIN_X - PAD, 0, ROOM_MIN_Z - PAD], max: [ROOM_MAX_X + PAD, WALL_H, ROOM_MIN_Z] },
        // North wall
        { min: [ROOM_MIN_X - PAD, 0, ROOM_MAX_Z], max: [ROOM_MAX_X + PAD, WALL_H, ROOM_MAX_Z + PAD] },
      ],
      enableSplat: false,
      enablePano: false,
      backdropColor: "#0d1014",
      exposure: 1.0,
      splat: {},
      // Interactive PC on the desk with a sticky-note password. Position/rotation
      // refined via a live raycast onto the real desk surface after deploy.
      deskPc: {
        position: [0.2, 0, 1.7],
        rotationDeg: 180,
        scale: 1,
        screenTitle: "NorthBay Financial",
        userName: "A. Morgan",
        password: "Summer2024!",
        stickyCaption: "portal login",
      },
      // Wall-mounted TV in the meeting room (west end). The panel mesh (X05_TV)
      // is centred at [-12.32, 1.8, 3.86], 3.0m wide (Z) x 1.5m tall, facing +X
      // into the room. The image plane sits just in front of it.
      conferenceTv: {
        screenCenter: [-12.3, 1.8, 3.86],
        width: 2.7,
        height: 1.45,
        rotationDeg: 90,
        imageUrl: "/cdn/sim/office-vr/tv-cyberfrog-clouds.png",
      },
    },
    props: [],
    waypoints: [
      {
        // Spawn in the reception / open area, facing the desk cluster.
        id: "wp_spawn",
        position: [1, 1.65, -0.5],
        target: [2.4, 1.15, 5],
        fov: 62,
        durationS: 0,
      },
    ],
    initialWaypointId: "wp_spawn",
  },

  devices: [],

  voiceAttackers: {
    walk_poc: {
      persona: {
        name: "PoC",
        role: "Walk",
        pretext: "it_support",
        language: "en",
        voiceId: "onyx",
      },
      goal: "Walk the office.",
      opening: "",
      allowedTopics: [],
      scoringEvents: [],
      forbidden: [],
      maxDurationS: 0,
    },
  },

  initialBeatId: "b_intro",

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Walkable Office",
      kicker: "Free-roam \u00b7 Practice",
      highlight: "conference room",
      guide: {
        title: "Walkable Office",
        body:
          "Walk the office freely: W/A/S/D (or the on-screen pad) to move, drag or capture " +
          "the mouse to look. Two tasks: 1) sign in to the workstation and open the browser, " +
          "then 2) walk to the conference room and switch on the TV.",
      },
    },
    {
      id: "b_walk",
      type: "camera",
      waypointId: "wp_spawn",
      hold: true,
    },
  ],

  edges: [{ from: "b_intro", to: "b_walk" }],
};
